import { store } from 'redux/store';
import { State } from 'redux/initialState';
import { useTranslate } from 'hooks/useTranslate';
import { FC, useState } from 'react';

import { useSelector } from 'react-redux';

import { v4 as uuidv4 } from 'uuid';

import { EMAIL_PATTERN } from 'constants/defaultConstants';
import { Partner } from 'types';

import { generateEmail } from 'utils/filters';
import checkIsValidEmail from 'utils/checkIsValidEmail';
import normalizeCif from 'utils/normalizeCif';
import normalizeEmail from 'utils/normalizeEmail';
import errorSwal from 'utils/errorSwal';
import successSwal from 'utils/successSwal';
import infoSwal from 'utils/infoSwal';

import { checkPartnerExists } from 'modules/partners/api/checkPartnerExists';
import { addPartner } from 'modules/partners/redux/partnerActions';
import PartnerTypeahead from '../../PartnerTypeahead';

import InputError from '../../InputError';
import CustomLoading from '../../CustomLoading';

import '../Modals.scss';

type AddPartnerEmbeddedModalProps = {
  onCreate: (addedPartner: Partner) => void; // eslint-disable-line
  onClose: VoidFunction; // eslint-disable-line
  date?: string | Date;
  isDraft?: boolean;
};

const AddPartnerEmbeddedModal: FC<AddPartnerEmbeddedModalProps> = ({
  onCreate,
  onClose,
  date = new Date(),
  isDraft = false,
}: AddPartnerEmbeddedModalProps) => {
  const { t } = useTranslate();

  const user = useSelector((state: State) => state?.user);
  const actualSociety = useSelector(
    (state: State) => state?.society?.actualSociety
  );

  const [name, setName] = useState<string>('');
  const [cif, setCif] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [naturalPerson, setNaturalPerson] = useState(true);
  const [validEmail, setValidEmail] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  const generateAlternativeId = () =>
    uuidv4().replace(/-/g, '').substring(0, 24);

  const handleChangeName = (value: string) => {
    setName(value);
  };

  const handleChangeCif = (value: string) => {
    const normalicedCif = normalizeCif(value);
    setCif(normalicedCif);
  };

  const handleChangeEmail = (value: string) => {
    const normalicedEmail = normalizeEmail(value);
    setEmail(normalicedEmail);

    const isValid = checkIsValidEmail(normalicedEmail);
    setValidEmail(isValid);
  };

  const handleClose = () => {
    onClose();
  };

  const handleChangePersonValue = () => {
    setNaturalPerson((prev) => !prev);
  };

  // eslint-disable-next-line
  const onChangeCif = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    handleChangeCif(value);
  };

  // eslint-disable-next-line
  const onChangeEmail = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    handleChangeEmail(value);
  };

  const createPartnerData = () => ({
    name,
    email: email !== '' ? email : generateEmail(actualSociety),
    date,
    society: actualSociety._id,
    societyName: actualSociety.name,
    userEmail: user.email,
    cif,
    user: user['_id'],
    isNaturalPerson: naturalPerson,
  });

  const handleSavePartner = async () => {
    try {
      setIsLoading(true);

      const partnerData = createPartnerData();

      const partnerExists = await store.dispatch(
        checkPartnerExists(actualSociety?.['_id'], cif)
      );

      if (partnerExists) {
        infoSwal(t('PartnerWithThaCifAlreadyExists'));
        setIsLoading(false);
        return;
      }

      const resultPartner = await store.dispatch(addPartner(partnerData));
      if (resultPartner) {
        onCreate(resultPartner);
      }

      handleClose();
      setIsLoading(false);
      successSwal(t('PartnerAddedSuccessfully'));
    } catch (error) {
      setIsLoading(false);
      errorSwal(t('ErrorAddingPartner'));
      handleClose();
    }
  };

  return (
    <>
      <label
        className="form-label d-flex justify-between align-center"
        htmlFor="email-address"
      >
        <span>{t('NewPartner')}</span>
        <em className="icon ni ni-cross text-primary" onClick={handleClose} />
      </label>

      <div
        className="row"
        style={{
          marginBottom: '20px',
        }}
      >
        <div className="col d-flex">
          <div className="form-group col-4 mb-0 p-0">
            <div className="form-control-wrap">
              <PartnerTypeahead
                naturalPerson={naturalPerson}
                onChangeName={handleChangeName}
                onChangeCif={handleChangeCif}
                onChangeEmail={handleChangeEmail}
              />
            </div>
          </div>

          <div className="form-group col-4 mb-0 p-0">
            <div className="form-control-wrap">
              <div className="form-icon form-icon-left">
                <em className="icon ni ni-cc-alt2" />
              </div>
              <input
                type="text"
                className="form-control"
                value={cif}
                onChange={onChangeCif}
                placeholder="CIF/NIF"
                required
              />
            </div>
          </div>

          <div className="form-group col-4 mb-0 p-0">
            <div className="form-control-wrap">
              <div className="form-icon form-icon-left">
                <em className="icon ni ni-mail" />
              </div>
              <input
                type="email"
                name="email"
                className="form-control"
                value={email}
                onChange={onChangeEmail}
                placeholder={t('Email')}
                pattern={EMAIL_PATTERN}
              />
            </div>
            {!validEmail && <InputError errorMessage={t('InvalidEmail')} />}
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col d-flex">
          <div className="col-6">
            <div className="custom-control custom-radio">
              <input
                type="radio"
                className="custom-control-input"
                name="natural-person"
                id="natural-person"
                checked={naturalPerson}
                onChange={handleChangePersonValue}
              />
              <label className="custom-control-label" htmlFor="natural-person">
                {t('NaturalPerson')}
              </label>
            </div>
          </div>
          <div className="col-6">
            <div className="custom-control custom-radio">
              <input
                type="radio"
                className="custom-control-input"
                name="legal-person"
                id="legal-person"
                checked={!naturalPerson}
                onChange={handleChangePersonValue}
              />
              <label className="custom-control-label" htmlFor="legal-person">
                {t('LegalPerson')}
              </label>
            </div>
          </div>
        </div>
      </div>

      <button
        type="button"
        className="btn btn-block btn-primary mt-3 mb-4"
        disabled={!name || !cif || !validEmail}
        onClick={handleSavePartner}
      >
        {t('AddPartner')}
      </button>

      {isLoading && <CustomLoading />}
    </>
  );
};

export default AddPartnerEmbeddedModal;
